import { Activity, Download, LucideIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { encodeBase64 } from "../../../../utils/config";
import { getApiErrorMessage } from "../../../../utils/utils";
import { exportPipelineStatusPdf } from "../../core/_request";
import {
  IDashboardFilterData,
  PipelineDepartmentData,
  PipelineOverviewData,
  PipelineSectorData,
} from "../../core/_models";
import SkeletonCard from "../../apps/components/skeleton/SkeletonCard";
import { PipeLineStatusCharts } from "./pipe-linestatus";
import SectionHeader from "./SectionHeader";
import "./pipelineStatus.css";

export interface PipelineQuickStat {
  label: string;
  value: string | number;
  icon: LucideIcon;
  change: string | number;
  gradient: string;
  bgGradient: string;
}

interface PipelineStatusProps {
  loading: boolean;
  quickStats: PipelineQuickStat[];
  filters: IDashboardFilterData;
  pipelineOverviewGraph: PipelineOverviewData[];
  sectorChartData: PipelineSectorData;
  departmentChartData: PipelineDepartmentData;
}

const CARD_THEMES = [
  { background: "#eef4ff", iconBackground: "#e7efff", accent: "#3b82f6" },
  { background: "#fff7ed", iconBackground: "#ffedd5", accent: "#f59e0b" },
  { background: "#f0fdf4", iconBackground: "#dcfce7", accent: "#22c55e" },
  { background: "#f5f3ff", iconBackground: "#ede9fe", accent: "#8b5cf6" },
];

const formatCount = (value: string | number): string => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  return numeric.toLocaleString();
};

const projectLink = (label: string, filters: IDashboardFilterData): string =>
  `/projects/?label=${encodeBase64(label)}&dashboard=${encodeBase64(
    "Pipeline Status"
  )}&url=${encodeBase64("dashboard")}&filter=${encodeBase64(JSON.stringify(filters))}`;

const PipelineStatus = ({
  loading,
  quickStats,
  filters,
  pipelineOverviewGraph,
  sectorChartData,
  departmentChartData,
}: PipelineStatusProps) => {
  const { showSnackbar } = useSnackbar();
  const [exporting, setExporting] = useState(false);
  const maxStat = Math.max(...quickStats.map((stat) => Number(stat.value) || 0), 0);

  const handleExport = useCallback(async () => {
    if (exporting) {
      return;
    }

    setExporting(true);
    try {
      const response = await exportPipelineStatusPdf(filters);
      const fileUrl = response.data?.url ?? "";

      if (response.status !== "ok" || !fileUrl) {
        showSnackbar(response.message || "Failed to prepare the export", "danger");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      showSnackbar(getApiErrorMessage(error, "Failed to prepare the export"), "danger");
    } finally {
      setExporting(false);
    }
  }, [exporting, filters, showSnackbar]);

  return (
    <div className="tab-pane active h-100 pipeline-status">
      <div className="p-4 p-sm-5 px-sm-10">
        <SectionHeader
          title="Pipeline Status"
          description="Monitor and manage government infrastructure projects across the Western Cape"
          icon={Activity}
          action={(
            <button
              type="button"
              className="pipeline-export-btn"
              onClick={handleExport}
              disabled={loading || exporting}
            >
              <Download size={16} aria-hidden />
              {exporting ? "Preparing export" : "Export"}
            </button>
          )}
        />

        <div className="pipeline-stats">
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
              <SkeletonCard
                key={index}
                wrapperClassName=""
                className="mb-0"
                titleWidth="100%"
                valueWidth="100%"
                showIcon={true}
                lines={2}
              />
            ))
            : quickStats.map((stat, index) => {
              const theme = CARD_THEMES[index] ?? CARD_THEMES[0];
              const share = maxStat === 0 ? 0 : ((Number(stat.value) || 0) / maxStat) * 100;
              const destination = index === 0 ? "#" : projectLink(stat.label, filters);
              const content = (
                <>
                  <div className="pipeline-stat__top">
                    <p className="pipeline-stat__label">{stat.label}</p>
                    <div className="pipeline-stat__icon" style={{ background: theme.iconBackground, color: theme.accent }}>
                      <stat.icon size={18} aria-hidden />
                    </div>
                  </div>
                  <p className="pipeline-stat__value">{formatCount(stat.value)}</p>
                  <p className="pipeline-stat__desc">{String(stat.change)}</p>
                  <div className="pipeline-stat__track">
                    <div className="pipeline-stat__bar" style={{ width: `${share}%`, background: theme.accent }} />
                  </div>
                </>
              );

              if (destination === "#") {
                return (
                  <div key={stat.label || index} className="pipeline-stat" style={{ background: stat.bgGradient }}>
                    {content}
                  </div>
                );
              }

              return (
                <Link key={stat.label || index} to={destination} className="pipeline-stat" style={{ background: stat.bgGradient }}>
                  {content}
                </Link>
              );
            })}
        </div>

        <PipeLineStatusCharts
          series={pipelineOverviewGraph}
          sectorChartData={sectorChartData}
          departmentChartData={departmentChartData}
          filters={filters}
          loading={loading}
        />
      </div>
    </div>
  );
};

export { PipelineStatus };
